import React from 'react';
import {
    Plug, Mail, Calendar, CheckSquare, StickyNote, FolderOpen,
    Shield, Check,
} from 'lucide-react';

// ============================================================================
// Tool Recommendations by Category
// ============================================================================
interface ToolRecommendation {
    id: string;
    name: string;
    description: string;
    category: string;
    icon: React.ElementType;
    riskLevel: 'low' | 'medium' | 'high';
    defaultScopes: string[];
    recommended: boolean;
}

const TOOL_REC_MAP: Record<string, ToolRecommendation[]> = {
    'Executive / Business': [
        { id: 'email.draft', name: 'Email Draft', description: 'Draft professional emails', category: 'Email', icon: Mail, riskLevel: 'medium', defaultScopes: ['read'], recommended: true },
        { id: 'calendar.read', name: 'Calendar Read', description: 'View schedule & availability', category: 'Calendar', icon: Calendar, riskLevel: 'low', defaultScopes: ['read'], recommended: true },
        { id: 'calendar.create', name: 'Calendar Create', description: 'Schedule meetings', category: 'Calendar', icon: Calendar, riskLevel: 'medium', defaultScopes: ['read', 'write'], recommended: true },
        { id: 'notes.create', name: 'Notes Create', description: 'Create meeting notes', category: 'Notes', icon: StickyNote, riskLevel: 'low', defaultScopes: ['read', 'write'], recommended: true },
        { id: 'files.summarize', name: 'Files Summarize', description: 'Summarize documents', category: 'Files', icon: FolderOpen, riskLevel: 'low', defaultScopes: ['read'], recommended: false },
    ],
    'Customer Support': [
        { id: 'tasks.read', name: 'Tasks Read', description: 'View support tickets', category: 'Tasks', icon: CheckSquare, riskLevel: 'low', defaultScopes: ['read'], recommended: true },
        { id: 'tasks.create', name: 'Tasks Create', description: 'Create tickets', category: 'Tasks', icon: CheckSquare, riskLevel: 'medium', defaultScopes: ['read', 'write'], recommended: true },
        { id: 'email.draft', name: 'Email Draft', description: 'Draft customer replies', category: 'Email', icon: Mail, riskLevel: 'medium', defaultScopes: ['read'], recommended: true },
        { id: 'notes.search', name: 'Notes Search', description: 'Search knowledge base', category: 'Notes', icon: StickyNote, riskLevel: 'low', defaultScopes: ['read'], recommended: true },
    ],
    'Creative / Content': [
        { id: 'notes.create', name: 'Notes Create', description: 'Create content drafts', category: 'Notes', icon: StickyNote, riskLevel: 'low', defaultScopes: ['read', 'write'], recommended: true },
        { id: 'files.summarize', name: 'Files Summarize', description: 'Summarize research', category: 'Files', icon: FolderOpen, riskLevel: 'low', defaultScopes: ['read'], recommended: true },
        { id: 'files.list', name: 'Files List', description: 'Browse content library', category: 'Files', icon: FolderOpen, riskLevel: 'low', defaultScopes: ['read'], recommended: true },
    ],
    'default': [
        { id: 'email.draft', name: 'Email Draft', description: 'Draft emails with AI help', category: 'Email', icon: Mail, riskLevel: 'medium', defaultScopes: ['read'], recommended: true },
        { id: 'calendar.read', name: 'Calendar Read', description: 'View your schedule', category: 'Calendar', icon: Calendar, riskLevel: 'low', defaultScopes: ['read'], recommended: true },
        { id: 'notes.create', name: 'Notes Create', description: 'Create and save notes', category: 'Notes', icon: StickyNote, riskLevel: 'low', defaultScopes: ['read', 'write'], recommended: true },
        { id: 'tasks.read', name: 'Tasks Read', description: 'View tasks', category: 'Tasks', icon: CheckSquare, riskLevel: 'low', defaultScopes: ['read'], recommended: false },
        { id: 'files.summarize', name: 'Files Summarize', description: 'Summarize documents', category: 'Files', icon: FolderOpen, riskLevel: 'low', defaultScopes: ['read'], recommended: false },
    ],
};

const RISK_COLORS: Record<string, string> = {
    low: 'text-emerald-400 bg-emerald-500/10',
    medium: 'text-amber-400 bg-amber-500/10',
    high: 'text-orange-400 bg-orange-500/10',
};

// ============================================================================
// Component
// ============================================================================
interface IntegrationsOnboardingStepProps {
    replicaCategory?: string;
    selectedTools: string[];
    onToolsChange: (tools: string[]) => void;
}

export default function IntegrationsOnboardingStep({ replicaCategory, selectedTools, onToolsChange }: IntegrationsOnboardingStepProps) {
    const recommendations = TOOL_REC_MAP[replicaCategory || ''] || TOOL_REC_MAP['default'];

    const toggleTool = (toolId: string) => {
        if (selectedTools.includes(toolId)) {
            onToolsChange(selectedTools.filter(t => t !== toolId));
        } else {
            onToolsChange([...selectedTools, toolId]);
        }
    };

    const selectRecommended = () => {
        const recommended = recommendations.filter(t => t.recommended).map(t => t.id);
        onToolsChange(recommended);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <div className="flex items-center gap-2 mb-2">
                    <Plug size={20} className="text-hig-accent" />
                    <h3 className="text-lg font-semibold text-hig-text-primary">Tools & Integrations</h3>
                </div>
                <p className="text-sm text-hig-text-secondary">
                    Choose which tools your Replica can use. All tools default to <span className="text-emerald-400 font-medium">read-only</span> scopes for safety. You can adjust permissions later in Integrations.
                </p>
            </div>

            {/* Quick select recommended */}
            <button onClick={selectRecommended} className="flex items-center gap-2 px-4 py-2 bg-hig-accent/10 text-hig-accent border border-hig-accent/20 rounded-lg text-sm font-medium hover:bg-hig-accent/15 transition-colors">
                <Check size={14} />
                Select recommended tools {replicaCategory ? `for ${replicaCategory}` : ''}
            </button>

            {/* Tools grid */}
            <div className="space-y-2">
                {recommendations.map(tool => {
                    const isSelected = selectedTools.includes(tool.id);
                    const Icon = tool.icon;

                    return (
                        <label key={tool.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-hig-accent/30 bg-hig-accent/5' : 'border-hig-border bg-hig-bg-tertiary hover:bg-hig-bg-elevated'}`}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleTool(tool.id)} className="rounded border-hig-border text-hig-accent focus:ring-hig-accent/20" />
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-hig-accent/10 flex items-center justify-center flex-shrink-0">
                                <Icon size={16} className="text-hig-accent" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-hig-text-primary">{tool.name}</span>
                                    {tool.recommended && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-accent/10 text-hig-accent border border-hig-accent/20">Recommended</span>
                                    )}
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${RISK_COLORS[tool.riskLevel]}`}>{tool.riskLevel}</span>
                                </div>
                                <p className="text-xs text-hig-text-secondary mt-0.5">{tool.description}</p>
                            </div>
                            <div className="flex items-center gap-1">
                                {tool.defaultScopes.map(s => (
                                    <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-hig-bg-tertiary text-hig-text-tertiary capitalize">{s}</span>
                                ))}
                            </div>
                        </label>
                    );
                })}
            </div>

            {/* Sovereignty notice */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-hig-accent/10 border border-hig-accent/20">
                <Shield size={16} className="text-hig-accent flex-shrink-0 mt-0.5" />
                <div>
                    <p className="text-xs text-hig-accent">
                        <strong>Your tools, your sovereignty.</strong> Personal tools operate in your Core Memory layer. All actions are protected by the Decision Guardian™ and logged to the immutable audit chain.
                    </p>
                </div>
            </div>

            {/* Skip notice */}
            <p className="text-xs text-hig-text-tertiary text-center">
                You can skip this step and configure tools later from the Integrations tab.
            </p>
        </div>
    );
}
